import React from 'react';
import { ListSubheader, Menu, MenuItem, Typography } from '@mui/material';
import { GhostButton } from './primitives';
import { useAttributeOptions } from './useAttributeOptions';
import { useTokenFieldContext } from './TokenFieldContext';
import type { AttributeScope } from '../../../Campaigns/guided/config/attributes';

/**
 * The admin insert-token picker: a small "Insert reference" button that opens a grouped menu of every
 * referenceable campaign attribute available at this authoring point, and inserts the chosen one as a
 * canonical `{{group.key}}` token. Seam-agnostic — the same component emits the same `{{…}}` spelling
 * whether the target is an AI prompt template, a content-block textarea, or report prose — so authors
 * pick from a menu rather than memorizing token spellings, and a `{{…}}` resolves identically wherever
 * it lands (screen / PDF / Word / AI grounding).
 *
 * Options come from {@link useAttributeOptions} (DB-driven: unauthored reference groups are absent).
 * Each item shows the human label, the exact token it inserts, and any legacy `[Bracketed]` alias.
 */
export function InsertTokenMenu(props: {
  campaignId: number | undefined;
  onInsert: (token: string) => void;
  /** The order of the step being authored; hides not-yet-available dynamic options. Omit = unrestricted. */
  uptoStepOrder?: number;
  /** Authoring scope for the group filter; defaults to the ambient {@link TokenFieldContext} scope. */
  scope?: AttributeScope;
  label?: string;
  disabled?: boolean;
}): JSX.Element {
  const { campaignId, onInsert, uptoStepOrder, scope, label, disabled } = props;
  const ctx = useTokenFieldContext();
  const groups = useAttributeOptions(campaignId, { uptoStepOrder, scope: scope ?? ctx.scope });
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const pick = (token: string) => {
    onInsert(token);
    setAnchorEl(null);
  };

  // MUI's Menu takes a flat child list; interleave a subheader per group with its option items.
  const items: React.ReactNode[] = [];
  groups.forEach((g) => {
    items.push(
      <ListSubheader key={`h-${g.group}`} disableSticky>
        {g.groupLabel}
      </ListSubheader>,
    );
    g.options.forEach((opt, i) => {
      items.push(
        <MenuItem
          key={`${g.group}-${i}-${opt.token}`}
          onClick={() => pick(opt.token)}
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25, py: 0.75 }}
        >
          <Typography variant="body2">{opt.label}</Typography>
          <Typography variant="caption" sx={{ opacity: 0.7, fontFamily: 'monospace' }}>
            {opt.token}
            {opt.legacyAlias ? ` · ${opt.legacyAlias}` : ''}
          </Typography>
        </MenuItem>,
      );
    });
  });

  return (
    <>
      <GhostButton disabled={disabled || items.length === 0} onClick={(e) => setAnchorEl(e.currentTarget)}>
        {label ?? 'Insert reference'}
      </GhostButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        MenuListProps={{ dense: true }}
        PaperProps={{ style: { maxHeight: 420 } }}
      >
        {items.length > 0 ? items : <MenuItem disabled>No references available</MenuItem>}
      </Menu>
    </>
  );
}

export default InsertTokenMenu;
